import { Flex, Label, useStyles } from '@archipro-design/aria';
import { useHandleTopNavTransitions } from '@archipro-website/top-navigation';
import React, { useRef } from 'react';

import * as S from './TitleSection.desktop.style';
import { useBoolean } from 'ahooks';
import { trackBreadcrumbClick } from '~/modules/tracking/util/trackBreadcrumbClick';
import { useTracker } from '@archipro-website/tracker';
import { Link } from '@remix-run/react';
import type { DirectoryPageMeta } from '~/modules/category/type';

interface TitleSectionProps {
    categoryDescription: string;
    category: string;
    itemsInCategory: number;
    rootCategory?: string;
    showCategoryTitle?: boolean;
    isRoot?: boolean;
    directoryPageMeta: DirectoryPageMeta;
}
const CATEGORY_DESCRIPTION =
    "Find everything you need to build, whether it's a tap or a cladding system. Connect to purchase directly from New Zealand's leading product suppliers.";

export const TitleSectionDesktop = ({
    categoryDescription,
    category,
    rootCategory,
    itemsInCategory,
    showCategoryTitle = true,
    isRoot,
    directoryPageMeta,
}: TitleSectionProps) => {
    const filterRef = useRef<HTMLDivElement>(null);
    const filterState = useHandleTopNavTransitions(filterRef);
    const [viewAllDescription, descriptionState] = useBoolean(false);

    const tracker = useTracker();
    const { css } = useStyles({
        filterState,
        viewAllDescription,
        showCategoryTitle,
    });

    const GiantText = isRoot ? (
        <h1 className="text-62.5 font-normal -ml-5 leading-none tracking-[-0.04em] text-black inline relative">
            {rootCategory ?? category}
        </h1>
    ) : (
        <div
            aria-hidden={true}
            className="inline relative before:content-[attr(data-giant-text-content)] before:inline before:relative before:text-gray-300 before:leading-none before:tracking-[-0.04em] before:text-62.5 before:-ml-5 before:pointer-events-none"
            data-giant-text-content={rootCategory ?? category}
        />
    );

    const crumbs = [
        {
            href: '/',
            label: 'Home',
        },
        ...directoryPageMeta.breadcrumbs.map((b) => ({
            href: b.href,
            label: b.label,
        })),
    ];

    const breadcrumbs = (
        <div className="flex items-center gap-3.5">
            {crumbs.map((breadcrumb, index) => (
                <React.Fragment key={breadcrumb.href}>
                    <Link
                        to={breadcrumb.href}
                        onClick={() => {
                            trackBreadcrumbClick(tracker, breadcrumb.href);
                        }}
                    >
                        <span className="text-2.5 font-medium uppercase tracking-[0.05rem] text-[#848484] hover:text-gray-700">
                            {breadcrumb.label}
                        </span>
                    </Link>
                    {index !== crumbs.length - 1 && (
                        <span className="text-2.5 font-medium uppercase tracking-[0.05rem] text-[#848484]">{`/`}</span>
                    )}
                </React.Fragment>
            ))}
        </div>
    );

    return (
        <div className={css(S.PageTopSectionWrapper)}>
            {breadcrumbs}
            <Flex space="between" className={css(S.PageTopSection)}>
                {showCategoryTitle && (
                    <div className={css(S.TitleWrapper)}>
                        {GiantText}
                        <div className="absolute m-2 align-top text-black text-lg inline-flex flex-col-reverse w-70">
                            {!isRoot && (
                                <h1 className="inline-block text-lg m-0">
                                    {category}
                                </h1>
                            )}
                            <div className="text-lg">
                                {itemsInCategory?.toLocaleString()}
                            </div>
                        </div>
                    </div>
                )}
                <div className={css(S.CategoryDescriptionWrapper)}>
                    <Label
                        variant="02"
                        maximumLines={viewAllDescription ? 0 : 6}
                        dangerouslySetInnerHTML={{
                            __html:
                                `<p>${categoryDescription}</p>` ||
                                CATEGORY_DESCRIPTION,
                        }}
                        onClick={() => descriptionState.setTrue()}
                    />
                </div>
            </Flex>
        </div>
    );
};
